/**
 * Contenido editorial del sitio (única fuente de textos visibles).
 */

export interface NavLink {
  readonly href: string;
  readonly label: string;
}

export interface FeatureItem {
  readonly title: string;
  readonly description: string;
}

export interface AudienceBlock {
  readonly title: string;
  readonly bullets: readonly string[];
}

/** Comentarios tipo testimonio (pool para mostrar 3 aleatorios). */
export interface AppTestimonial {
  readonly text: string;
  readonly author: string;
  readonly roleLabel: string;
  /** Valoración 1–5; si se omite, en la UI se asume 5. */
  readonly rating?: 1 | 2 | 3 | 4 | 5;
}

/** Cifras públicas (fallback hasta conectar API). */
export interface PublicStats {
  readonly collaborators: number;
  readonly users: number;
  readonly businesses: number;
}

export interface ContactEmailLine {
  readonly address: string;
  readonly label: string;
}

export interface VehicleScopeCategory {
  readonly id: 'bike' | 'ebike' | 'light' | 'ice' | 'ev' | 'heavy' | 'equipment';
  readonly title: string;
  readonly caption: string;
}

export const SITE = {
  name: 'Servave',
  tagline: 'Asistencia mecánica y en ruta cuando la necesitas.',
  description:
    'Conecta con colaboradores y compañías de confianza. Solicita servicios, coordina por chat en tiempo real y gestiona tu experiencia desde un solo lugar.',

  /** Texto alternativo de la captura del producto en el hero (`public/hero-product.png`). */
  heroProductAlt:
    'Pantalla de la app Servave: seguimiento de ruta en tiempo real con mapa, ETA y estado del servicio.',

  /** Texto sobre disponibilidad móvil; Android primero. */
  storesHeadline: 'Disponible en Google Play (Android) y en App Store (iOS)',
  playStoreUrl: 'https://play.google.com/store/apps/details?id=com.servave.app',
  appStoreUrl: 'https://apps.apple.com/co/app/servave/id6771543826',

  /** Enlaces visibles en una sola fila (los más consultados). */
  navPrimary: [
    { href: '#inicio', label: 'Inicio' },
    { href: '#funciones', label: 'Funciones' },
    { href: '#costos', label: 'Costos' },
    { href: '#soluciones-empresas', label: 'Negocio' },
  ] as const satisfies readonly NavLink[],

  /** Resto de secciones bajo el menú desplegable «Más». */
  navMoreLabel: 'Más',
  navMore: [
    { href: '#para-quien', label: 'Para quién' },
    { href: '#presta-asistencia', label: 'Presta servicios' },
    { href: '#colombia', label: 'Latinoamérica' },
    { href: '#comunidad', label: 'Comunidad' },
    { href: '#comentarios', label: 'Opiniones' },
  ] as const satisfies readonly NavLink[],

  navContact: { href: '#contacto', label: 'Contacto' } as const satisfies NavLink,

  /**
   * Login de administradores (`admin.html`). El envío del formulario debe enlazarse a tu API / IAM.
   */
  adminLogin: {
    page: 'admin.html',
    headerLabel: 'Acceso administradores',
    documentTitle: 'Ingreso administradores — Servave',
    cardTitle: 'Panel administrativo',
    cardSubtitle:
      'Acceso exclusivo para personal autorizado. Esta pantalla es la puerta de entrada al módulo interno.',
    phoneLabel: 'Línea telefónica',
    passwordLabel: 'Contraseña',
    submitLabel: 'Ingresar',
    submitPendingLabel: 'Ingresando…',
    backLabel: 'Volver al sitio público',
    footerHint: '¿Problemas para entrar? Escríbenos a gerencia@servave.com.',
    errorNotAdmin:
      'Esta cuenta no tiene rol de administrador. Solo personal autorizado puede acceder al panel.',
    errorGeneric: 'No se pudo iniciar sesión. Revisa la línea y la contraseña.',
    successHint: 'Sesión iniciada. Puedes cerrar esta pestaña o volver al sitio público.',
  } as const,

  /**
   * Login de empresas (`business.html`). Mismo flujo JWT que la app móvil con `isCompany: true`.
   */
  businessLogin: {
    page: 'business.html',
    headerLabel: 'Acceso empresas',
    documentTitle: 'Ingreso empresas — Servave',
    cardTitle: 'Panel empresarial',
    cardSubtitle:
      'Acceso para compañías registradas en Servave. Consulta colaboradores, servicios y operación en tiempo real.',
    nitLabel: 'NIT de la empresa',
    nitPlaceholder: '900123456-7',
    passwordLabel: 'Contraseña',
    submitLabel: 'Ingresar',
    submitPendingLabel: 'Ingresando…',
    backLabel: 'Volver al sitio público',
    footerHint: '¿Problemas para entrar? Escríbenos a comercial@servave.com.',
    errorNotBusiness:
      'Esta cuenta no tiene rol de empresa. Verifica el NIT y que tu compañía esté activa en Servave.',
    errorGeneric: 'No se pudo iniciar sesión. Revisa el NIT y la contraseña.',
  } as const,

  pricingTitle: 'Costos por perfil',
  pricingSubtitle:
    'Resumen transparente: sin sorpresas al registrarte. Los valores de suscripción empresarial pueden ajustarse según plan.',

  pricingTiers: [
    {
      id: 'colaboradores',
      badge: 'Colaboradores',
      priceLine: 'Registro gratuito',
      highlight: '15% de comisión sobre el valor del servicio',
      details:
        'Sin costo de alta. Se aplica una comisión adicional del 15% al valor del servicio prestado a través de la plataforma.',
    },
    {
      id: 'clientes',
      badge: 'Clientes',
      priceLine: '100% gratis',
      highlight: 'Registro sin costo',
      details: 'Crear tu cuenta y usar el aplicativo como usuario cliente no tiene ningún cobro: completamente gratuito.',
    },
    {
      id: 'empresas',
      badge: 'Empresas',
      priceLine: 'Desde $100.000 / mes',
      highlight: 'Suscripción mensual',
      details:
        'Planes de suscripción mensual para presencia B2B en Servave, desde cien mil pesos colombianos (COP), según alcance y módulos contratados.',
    },
  ] as const,

  enterpriseTitle: 'Servicios para empresas en Servave',
  enterpriseSubtitle:
    'Herramientas pensadas para marcas, distribuidores y puntos de venta que quieren crecer dentro del ecosistema del aplicativo.',

  enterpriseFeatures: [
    'Publicación y gestión de productos en la plataforma.',
    'Ubicaciones exactas de puntos de venta y presencia en mapa.',
    'Canal de contacto por WhatsApp hacia tu negocio.',
    'Visualización y difusión de promociones.',
    'Notificaciones push a clientes y colaboradores que usan el aplicativo.',
    'Mensajes y avisos asociados a productos creados o actualizados.',
    'Y más funciones de visibilidad y engagement según tu plan.',
  ] as const,

  colombiaEyebrow: 'Cobertura en Latinoamérica',
  colombiaTitle: 'Servave está disponible en toda Latinoamérica',
  colombiaDescription:
    'Estamos construyendo una red que conecte clientes, colaboradores y empresas en toda la región: desde las principales ciudades hasta rutas y zonas donde hoy cuesta encontrar asistencia confiable. Un mismo aplicativo, alcance latinoamericano.',

  heroCtaPrimary: 'Descubre Servave',
  heroCtaSecondary: 'Ver funciones',

  featuresIntro: 'Todo lo que necesitas en carretera y taller',

  features: [
    {
      title: 'Solicitud por mapa',
      description:
        'Encuentra servicios cercanos y solicita asistencia según categoría y ubicación, con flujo guiado en la app.',
    },
    {
      title: 'Chat en tiempo real',
      description:
        'Coordina con tu colaborador con mensajería integrada al servicio: menos llamadas, más claridad.',
    },
    {
      title: 'Colaboradores y empresas',
      description:
        'Clientes, talleres y empresas en un mismo ecosistema: visibilidad, historial y herramientas operativas.',
    },
    {
      title: 'Asistencia SOS',
      description:
        'Acceso a flujos de ayuda cuando más lo necesitas, alineados con tu sesión y tu contexto de servicio.',
    },
  ] as const satisfies readonly FeatureItem[],

  audiencesIntro: 'Una plataforma, dos experiencias',

  audiences: {
    clients: {
      title: 'Para clientes',
      bullets: [
        'Solicita servicios y sigue el estado de tu reserva.',
        'Chatea con el colaborador asignado.',
        'Gestiona tu perfil, favoritos e historial.',
      ],
    },
    collaborators: {
      title: 'Para colaboradores',
      bullets: [
        'Cualquier persona puede ofrecer asistencia vehicular y generar dinero extra según los servicios que habilite.',
        'Recibe solicitudes, gestiona disponibilidad y cobra por trabajos coordinados en la plataforma.',
        'Chats por servicio, seguimiento operativo y herramientas para configurar tu oferta.',
      ],
    },
  } as const satisfies Record<string, AudienceBlock>,

  assistOfferEyebrow: 'Oportunidad para colaboradores',
  assistOfferTitle: 'Presta asistencia vehicular y genera ingresos extra',
  assistOfferBody:
    'Si quieres brindar servicios de asistencia en ruta o en taller, puedes registrarte como colaborador, definir qué ofreces y conectar con clientes que lo necesitan. Es una forma flexible de sumar ingresos adicionales, con reglas de comisión claras dentro del aplicativo.',

  vehicleScopeTitle: 'Asistencia para todo tipo de vehículo',
  vehicleScopeIntro:
    'Servave cubre movilidad urbana y en ruta: bicicletas (incluidas eléctricas), micromovilidad, motos y autos, combustión, híbridos o eléctricos, vehículos pesados y equipos o maquinaria — según lo que habilites en tu perfil.',
  vehicleScopeCategories: [
    {
      id: 'bike',
      title: 'Bicicletas y micromovilidad',
      caption: 'Tradicional y asistencia mecánica liviana.',
    },
    {
      id: 'ebike',
      title: 'Bicicletas eléctricas',
      caption: 'E-bike, patinetes y monopatines eléctricos.',
    },
    {
      id: 'light',
      title: 'Motos, autos y livianos',
      caption: 'Motocicletas, particulares y comercial liviano.',
    },
    {
      id: 'ice',
      title: 'Combustión',
      caption: 'Motores gasolina y diésel.',
    },
    {
      id: 'ev',
      title: 'Híbridos y eléctricos',
      caption: 'HEV, PHEV y BEV.',
    },
    {
      id: 'heavy',
      title: 'Carros pesados',
      caption: 'Camiones, buses, tractocamiones y carga.',
    },
    {
      id: 'equipment',
      title: 'Equipos, maquinarias y otros',
      caption: 'Maquinaria amarilla, industrial y equipos especiales.',
    },
  ] as const satisfies readonly VehicleScopeCategory[],

  /** Mostrar en la sección de métricas; sustituible vía `VITE_STATS_JSON_URL`. */
  statsFallback: {
    collaborators: 128,
    users: 4520,
    businesses: 36,
  } as const satisfies PublicStats,

  statsSectionTitle: 'Comunidad Servave',
  statsSectionSubtitle:
    'Cifras orientativas de usuarios activos en la plataforma. Pueden actualizarse desde tu API pública.',

  statsLabels: {
    collaborators: 'Colaboradores registrados',
    users: 'Usuarios registrados',
    businesses: 'Empresas registradas',
  } as const,

  commentsSectionTitle: 'Opiniones desde la app',
  commentsSectionSubtitle:
    'Ejemplos de valoraciones y comentarios que podrías ver en el aplicativo móvil (se muestran al azar).',
  commentsRefreshLabel: 'Ver otros comentarios',

  /**
   * Pool amplio para rotación aleatoria en la landing.
   * Sustituir por datos reales cuando expongas endpoint público.
   */
  testimonials: [
    {
      text: 'Pedí grúa desde el mapa y en minutos ya iba coordinando por el chat. Muy claro todo el proceso.',
      author: 'Laura M.',
      roleLabel: 'Cliente',
      rating: 5,
    },
    {
      text: 'Como colaborador, recibir solicitudes ordenadas y chatear por servicio me ahorra llamadas perdidas.',
      author: 'Carlos R.',
      roleLabel: 'Colaborador',
      rating: 5,
    },
    {
      text: 'La empresa puede verse en el ecosistema y los clientes llegan con contexto. Nos ayuda a priorizar.',
      author: 'Distribuidora Norte',
      roleLabel: 'Empresa',
      rating: 4,
    },
    {
      text: 'El seguimiento del servicio en la app es mejor que mandar ubicación por WhatsApp cada rato.',
      author: 'Andrés P.',
      roleLabel: 'Cliente',
      rating: 5,
    },
    {
      text: 'Los avisos cuando el colaborador está en camino me dan tranquilidad en carretera.',
      author: 'Diana S.',
      roleLabel: 'Cliente',
      rating: 5,
    },
    {
      text: 'Configuré mis servicios y horarios; cuando llega una petición ya sé si puedo tomarla.',
      author: 'Miguel T.',
      roleLabel: 'Colaborador',
      rating: 4,
    },
    {
      text: 'Historial de servicios útil para volver a pedir al mismo taller cuando viajo.',
      author: 'Felipe G.',
      roleLabel: 'Cliente',
      rating: 4,
    },
    {
      text: 'Chat integrado al booking evita confusiones de números de reserva.',
      author: 'Taller Express',
      roleLabel: 'Empresa',
      rating: 5,
    },
    {
      text: 'En SOS encontré el flujo rápido cuando se me patinó el carro en lluvia.',
      author: 'Juliana V.',
      roleLabel: 'Cliente',
      rating: 5,
    },
    {
      text: 'Wallet y comisiones visibles: menos sorpresas al cerrar el mes.',
      author: 'Héctor L.',
      roleLabel: 'Colaborador',
      rating: 5,
    },
    {
      text: 'Me gusta ver colaboradores cercanos con el tipo de servicio que necesito.',
      author: 'Paola E.',
      roleLabel: 'Cliente',
      rating: 4,
    },
    {
      text: 'Registrar la empresa fue guiado; soporte entendió el modelo mixto taller + ruta.',
      author: 'Motores del Valle',
      roleLabel: 'Empresa',
      rating: 5,
    },
  ] as const satisfies readonly AppTestimonial[],

  cta: {
    title: 'Contacto',
    subtitle:
      'Escríbenos o comunícate con el equipo Servave. Estamos en Palmira, Valle del Cauca.',
    button: 'Volver arriba',
  },

  /** Datos corporativos (#contacto). */
  contact: {
    emails: [
      { label: 'Gerencia', address: 'gerencia@servave.com' },
      { label: 'Soporte técnico', address: 'soporte_tecnico@servave.com' },
      { label: 'Comercial', address: 'comercial@servave.com' },
    ] as const satisfies readonly ContactEmailLine[],
    phoneWhatsAppLabel: 'Teléfono y WhatsApp corporativo',
    phoneDisplay: '+57 304 5786398',
    /** Enlaces tel: y wa.me (Colombia +57, sin espacios). */
    phoneTel: '+573045786398',
    whatsAppDigits: '573045786398',
    location: 'Palmira, Valle del Cauca',
  },

  footer: {
    notice:
      'Servave es el producto digital de asistencia mecánica en ruta. Disponible en Google Play y App Store.',
    copyYear: 2026,
  },
} as const;
